import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getSettings,
  updateSettings,
} from "../../../services/settingsServices";
import { getAllGalleries } from "../../../services/gallerieServices";
import Button from "../../../components/Button/Button";
import { toast } from "react-toastify";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  FaSave,
  FaChevronLeft,
  FaChevronRight,
  FaMoon,
  FaSun,
  FaPlay,
  FaPause,
} from "react-icons/fa";
import HomeSectionsManager from "./HomeSectionsManager";

/* Quill */
const modules = {
  toolbar: [
    [{ header: "1" }, { header: "2" }, { font: [] }],
    [{ size: [] }],
    ["bold", "italic", "underline", "strike", "blockquote"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    ["link", "image", "video"],
    ["clean"],
  ],
};
const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "color",
  "background",
  "script",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

/* Helpers Preview */
const getTextAlignment = (align) => {
  switch (align) {
    case "left":
      return "text-left left-5 sm:left-10";
    case "right":
      return "text-right right-5 sm:right-10";
    default:
      return "";
  }
};

const HomeSettingsForm = () => {
  const [settings, setSettings] = useState({
    header_url1: "",
    header_texte1: "",
    header_url2: "",
    header_texte2: "",
    about_title: "",
    about_content: "",
    carrousel_id: "",
    logo_club: "",
  });

  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Preview state
  const [previewDark, setPreviewDark] = useState(false);
  const [slide, setSlide] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const intervalRef = useRef(null);

  const headerImages = useMemo(
    () => [
      {
        src: settings.header_url1?.trim() || "/img/header_placeholder_1.jpg",
        text: settings.header_texte1 || "",
        align: "left",
      },
      {
        src: settings.header_url2?.trim() || "/img/header_placeholder_2.jpg",
        text: settings.header_texte2 || "",
        align: "right",
      },
    ],
    [settings.header_url1, settings.header_texte1, settings.header_url2, settings.header_texte2]
  );

  useEffect(() => {
    const fetchSettingsAndGalleries = async () => {
      try {
        const [settingsData, galleriesData] = await Promise.all([
          getSettings(),
          getAllGalleries(),
        ]);
        if (Array.isArray(settingsData) && settingsData.length > 0) {
          setSettings((prev) => ({ ...prev, ...settingsData[0] }));
        }
        setGalleries(galleriesData || []);
      } catch (error) {
        console.error("Erreur lors de la récupération des données:", error);
        toast.error("Impossible de charger les paramètres.");
      } finally {
        setLoading(false);
      }
    };
    fetchSettingsAndGalleries();
  }, []);

  // Auto play slider (aperçu)
  useEffect(() => {
    if (!autoPlay) return;
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setSlide((s) => (s + 1) % headerImages.length);
    }, 8000);
    return () => clearInterval(intervalRef.current);
  }, [autoPlay, headerImages.length]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContentChange = (name, value) => {
    setSettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...settings,
        carrousel_id: settings.carrousel_id || null,
      };
      await updateSettings(payload);
      toast.success("Paramètres mis à jour avec succès");
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la mise à jour des paramètres");
    }
  };

  if (loading) {
    return (
      <section className="w-full">
        <div className="mx-auto max-w-7xl px-1 sm:px-1">
          <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
            <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8">
              <div className="h-8 w-64 bg-white/25 rounded animate-pulse" />
            </div>
            <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-24 rounded-xl bg-slate-100/80 dark:bg-slate-800/40 animate-pulse" />
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full">
      <div className="mx-auto max-w-7xl px-1 sm:px-1">
        <div className="rounded-2xl overflow-hidden shadow ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/70 dark:bg-slate-900/60 backdrop-blur">
          {/* HEADER */}
          <div className="bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-6 sm:px-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-white text-2xl sm:text-3xl font-bold tracking-tight">
                Page d’Accueil — Paramètres & Prévisualisation
              </h2>
              <p className="text-white/90 text-sm sm:text-[15px] mt-1">
                Modifiez le header, la section « À propos » et l’album carrousel, tout en visualisant le rendu.
              </p>
            </div>
            <Button
              text="Enregistrer"
              icon={FaSave}
              onClick={handleSubmit}
              className="!bg-white !text-slate-900 !hover:bg-white/90 !shadow-md !rounded-xl gap-2"
            />
          </div>

          {/* CONTENT: Form + Preview */}
          <div className="p-4 sm:p-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Formulaire */}
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Header images */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-3">
                  Header — Image 1
                </h3>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Lien de l’image</label>
                <input
                  type="url"
                  name="header_url1"
                  value={settings.header_url1}
                  onChange={handleChange}
                  placeholder="https://…"
                  inputMode="url"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
                <label className="mt-4 block text-sm font-medium text-slate-700 dark:text-slate-200">
                  Texte superposé
                </label>
                <div
                  className="mt-1
                  [&_.ql-toolbar]:rounded-t-xl [&_.ql-toolbar]:border-slate-300 dark:[&_.ql-toolbar]:border-slate-700
                  [&_.ql-toolbar]:bg-white/80 dark:[&_.ql-toolbar]:bg-slate-900/70
                  [&_.ql-container]:rounded-b-xl [&_.ql-container]:border-slate-300 dark:[&_.ql-container]:border-slate-700
                  [&_.ql-container]:bg-white/80 dark:[&_.ql-container]:bg-slate-900/70"
                >
                  <ReactQuill
                    value={settings.header_texte1}
                    onChange={(v) => handleContentChange("header_texte1", v)}
                    modules={modules}
                    formats={formats}
                    placeholder="<h1>Votre titre</h1><p>Votre sous-titre</p>"
                  />
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-3">
                  Header — Image 2
                </h3>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">Lien de l’image</label>
                <input
                  type="url"
                  name="header_url2"
                  value={settings.header_url2}
                  onChange={handleChange}
                  placeholder="https://…"
                  inputMode="url"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
                <label className="mt-4 block text-sm font-medium text-slate-700 dark:text-slate-200">
                  Texte superposé
                </label>
                <div
                  className="mt-1
                  [&_.ql-toolbar]:rounded-t-xl [&_.ql-toolbar]:border-slate-300 dark:[&_.ql-toolbar]:border-slate-700
                  [&_.ql-toolbar]:bg-white/80 dark:[&_.ql-toolbar]:bg-slate-900/70
                  [&_.ql-container]:rounded-b-xl [&_.ql-container]:border-slate-300 dark:[&_.ql-container]:border-slate-700
                  [&_.ql-container]:bg-white/80 dark:[&_.ql-container]:bg-slate-900/70"
                >
                  <ReactQuill
                    value={settings.header_texte2}
                    onChange={(v) => handleContentChange("header_texte2", v)}
                    modules={modules}
                    formats={formats}
                    placeholder="<h1>Votre titre</h1><p>Votre sous-titre</p>"
                  />
                </div>
              </div>

              {/* À propos */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-3">
                  Section « À propos »
                </h3>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Titre
                </label>
                <input
                  type="text"
                  name="about_title"
                  value={settings.about_title}
                  onChange={handleChange}
                  placeholder="Qui sommes-nous ?"
                  className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-400"
                />
                <label className="mt-4 block text-sm font-medium text-slate-700 dark:text-slate-200">
                  Contenu
                </label>
                <div
                  className="mt-1
                  [&_.ql-toolbar]:rounded-t-xl [&_.ql-toolbar]:border-slate-300 dark:[&_.ql-toolbar]:border-slate-700
                  [&_.ql-toolbar]:bg-white/80 dark:[&_.ql-toolbar]:bg-slate-900/70
                  [&_.ql-container]:rounded-b-xl [&_.ql-container]:border-slate-300 dark:[&_.ql-container]:border-slate-700
                  [&_.ql-container]:bg-white/80 dark:[&_.ql-container]:bg-slate-900/70"
                >
                  <ReactQuill
                    value={settings.about_content}
                    onChange={(v) => handleContentChange("about_content", v)}
                    modules={modules}
                    formats={formats}
                    placeholder="<p>Notre club...</p>"
                  />
                </div>
              </div>

              {/* Carrousel */}
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-4 sm:p-5">
                <h3 className="text-base font-semibold text-slate-800 dark:text-slate-100 mb-3">
                  Carrousel (album photo)
                </h3>
                <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Sélection de l’album
                </label>
                {galleries.length > 0 ? (
                  <select
                    name="carrousel_id"
                    value={settings.carrousel_id || ""}
                    onChange={(e) => {
                      const value = e.target.value === "" ? null : e.target.value;
                      setSettings((prev) => ({ ...prev, carrousel_id: value }));
                    }}
                    className="mt-1 w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white/80 dark:bg-slate-900/70 px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-400"
                  >
                    <option value="">❌ Ne pas afficher d’album</option>
                    {galleries.map((gallery) => (
                      <option key={gallery.id} value={gallery.id}>
                        {gallery.titre}
                      </option>
                    ))}
                  </select>
                ) : (
                  <p className="mt-1 text-sm text-slate-500">
                    Vous n’avez pas encore d’album photo.
                  </p>
                )}
                {settings.carrousel_id && (
                  <p className="mt-2 text-xs text-slate-600 dark:text-slate-400">
                    Album sélectionné :{" "}
                    <span className="font-semibold">
                      {galleries.find((g) => String(g.id) === String(settings.carrousel_id))?.titre ||
                        `#${settings.carrousel_id}`}
                    </span>
                  </p>
                )}
              </div>

              {/* Actions bas */}
              <div className="flex justify-end">
                <Button
                  text="Mettre à jour"
                  type="submit"
                  className="!rounded-xl gap-2"
                  icon={FaSave}
                />
              </div>
            </form>

            {/* Prévisualisation temps réel */}
            <div className="space-y-6">
              {/* Toggle Dark/Light + autoplay */}
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setAutoPlay((v) => !v)}
                  className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white/70 dark:bg-slate-900/70 px-3 py-2 text-sm inline-flex items-center gap-2 hover:bg-white/90 dark:hover:bg-slate-800"
                  title={autoPlay ? "Mettre en pause" : "Lancer le défilement"}
                >
                  {autoPlay ? <FaPause /> : <FaPlay />}
                  {autoPlay ? "Pause" : "Lecture"}
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewDark((d) => !d)}
                  className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white/70 dark:bg-slate-900/70 px-3 py-2 text-sm inline-flex items-center gap-2 hover:bg-white/90 dark:hover:bg-slate-800"
                >
                  {previewDark ? <FaSun /> : <FaMoon />}
                  {previewDark ? "Clair" : "Sombre"}
                </button>
              </div>

              {/* HEADER PREVIEW */}
              <div className={`${previewDark ? "dark" : ""}`}>
                <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40">
                  <div className="relative w-full h-[260px] sm:h-[320px] bg-slate-200 dark:bg-slate-800">
                    {headerImages.map((s, i) => (
                      <img
                        key={i}
                        src={s.src}
                        alt={`slide-${i}`}
                        className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
                          i === slide ? "opacity-100" : "opacity-0"
                        }`}
                        onError={(e) => {
                          e.currentTarget.src =
                            i === 0
                              ? "/img/header_placeholder_1.jpg"
                              : "/img/header_placeholder_2.jpg";
                        }}
                      />
                    ))}

                    {/* Overlay texte */}
                    <div
                      className={`absolute top-1/2 -translate-y-1/2 max-w-[80%] sm:max-w-[60%] text-white drop-shadow-xl ${getTextAlignment(
                        headerImages[slide].align
                      )}`}
                    >
                      <div
                        className="animate-fadein"
                        dangerouslySetInnerHTML={{ __html: headerImages[slide].text }}
                      />
                    </div>

                    {/* Controls */}
                    <button
                      type="button"
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-slate-900/40 hover:bg-slate-900/60 text-white rounded-full p-2"
                      onClick={() => setSlide((s) => (s - 1 + headerImages.length) % headerImages.length)}
                      title="Précédent"
                    >
                      <FaChevronLeft />
                    </button>
                    <button
                      type="button"
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-slate-900/40 hover:bg-slate-900/60 text-white rounded-full p-2"
                      onClick={() => setSlide((s) => (s + 1) % headerImages.length)}
                      title="Suivant"
                    >
                      <FaChevronRight />
                    </button>

                    {/* Bullets */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                      {headerImages.map((_, i) => (
                        <span
                          key={i}
                          className={`h-2.5 w-2.5 rounded-full border border-white/70 ${
                            i === slide ? "bg-white" : "bg-transparent"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* ABOUT PREVIEW */}
              <div className={`${previewDark ? "dark" : ""}`}>
                <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-5">
                  <section className="py-6 bg-gray-100 dark:bg-gray-700 dark:text-white rounded-xl">
                    <div className="container mx-auto px-4">
                      <div className="flex flex-wrap -mx-4 overflow-hidden items-center">
                        <div className="px-4 w-full lg:w-1/3 overflow-hidden my-4">
                          <img
                            src={settings.logo_club || "/img/club_logo_placeholder.png"}
                            alt="Logo Club"
                            className="block mx-auto drop-shadow-md max-h-40 object-contain"
                            onError={(e) => (e.currentTarget.src = "/img/club_logo_placeholder.png")}
                          />
                        </div>
                        <div className="font-montserrat px-4 lg:px-8 w-full lg:w-2/3 overflow-hidden my-4">
                          <h2 className="text-2xl sm:text-3xl font-extrabold mb-4">
                            {settings.about_title || "À propos de notre club"}
                          </h2>
                          <div
                            className="text-justify mb-2 leading-relaxed about_content prose prose-slate dark:prose-invert max-w-none"
                            dangerouslySetInnerHTML={{
                              __html:
                                settings.about_content ||
                                "<p>Ajoutez votre présentation ici.</p>",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </section>
                </div>
              </div>

              {/* CAROUSEL PREVIEW (indicatif) */}
              <div className={`${previewDark ? "dark" : ""}`}>
                <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/40 p-5">
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                    Carrousel (aperçu indicatif)
                  </h4>
                  {settings.carrousel_id ? (
                    <div className="text-sm text-slate-700 dark:text-slate-300">
                      Album sélectionné :{" "}
                      <span className="inline-flex items-center gap-2 rounded-lg px-2 py-1 bg-slate-100 dark:bg-slate-800">
                        <span className="h-2 w-2 rounded-full bg-violet-500 inline-block" />
                        {
                          galleries.find((g) => String(g.id) === String(settings.carrousel_id))
                            ?.titre
                        }
                      </span>
                      <p className="mt-2 text-xs opacity-80">
                        (Les images exactes s’afficheront sur la page d’accueil).
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">Aucun album sélectionné.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <HomeSectionsManager />

      {/* Styles locaux pour l’aperçu (animations légères) */}
      <style>{`
        .animate-fadein { animation: fadein .5s ease-out both; }
        @keyframes fadein { from { opacity: 0; transform: translateY(6px) } to { opacity: 1; transform: translateY(0) } }

        /* Placeholders */
        input::placeholder { color: #94a3b8; }
        .dark input::placeholder { color: #94a3b8; opacity: .7; }

        /* Apple-friendly text smoothing */
        html { -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
      `}</style>
    </section>
  );
};

export default HomeSettingsForm;
